import {
	ErgonomicToolMapper,
	ErgonomicToolsDTO,
	ErgonomicToolsName
} from '@/components/ui/Inputs/types/response/ergonomicTool';
import { StressLevelDTO } from '@/components/ui/Inputs/types/response/stressLevel';

interface StepKeyToolsInterface {
	calculateStressLevelScore(stressLevels: StressLevelDTO[], ergonomicTool: ErgonomicToolMapper): string | undefined;
}

export class StepKeyToolsService implements StepKeyToolsInterface {
	public calculateStressLevelScore(
		stressLevels: StressLevelDTO[],
		ergonomicTool: ErgonomicToolMapper
	): string | undefined {
		const activeTools = Object.keys(ergonomicTool).filter(
			(key) =>
				ergonomicTool[key as ErgonomicToolsName]?.isActive && ergonomicTool[key as ErgonomicToolsName]?.data
		) as ErgonomicToolsName[];

		if (activeTools.length === 0) return undefined;

		if (activeTools.length === 1) {
			return this.calculateSingleTool(activeTools[0], ergonomicTool[activeTools[0]].data, stressLevels);
		}

		return this.calculateWorstFromMultipleTools(activeTools, ergonomicTool, stressLevels);
	}

	private calculateSingleTool(
		toolName: ErgonomicToolsName,
		data: ErgonomicToolsDTO,
		stressLevels: StressLevelDTO[]
	): string | undefined {
		const stressLevel = this.getStressLevelForTool(toolName, data, stressLevels);
		return stressLevel?.id;
	}

	private calculateWorstFromMultipleTools(
		activeTools: ErgonomicToolsName[],
		ergonomicTool: ErgonomicToolMapper,
		stressLevels: StressLevelDTO[]
	): string | undefined {
		let worstStressLevel: StressLevelDTO | undefined;

		for (const toolName of activeTools) {
			const data = ergonomicTool[toolName]?.data;
			const stressLevel = this.getStressLevelForTool(toolName, data, stressLevels);
			if (stressLevel && (!worstStressLevel || stressLevel.score > worstStressLevel.score)) {
				worstStressLevel = stressLevel;
			}
		}

		return worstStressLevel?.id;
	}

	private getStressLevelForTool(
		toolName: ErgonomicToolsName,
		data: ErgonomicToolsDTO,
		stressLevels: StressLevelDTO[]
	): StressLevelDTO | undefined {
		if (!data) return undefined;

		switch (toolName) {
			case 'niosh':
				return this.calculateNiosh(data, stressLevels);
			case 'kim_mho':
				return this.calculateKimMho(data, stressLevels);
			case 'strain_index':
				return this.calculateStrainIndex(data, stressLevels);
			case 'kim_pp':
				return this.calculateKimPp(data, stressLevels);
			default:
				return undefined;
		}
	}

	private findStressLevel(
		stressLevels: StressLevelDTO[],
		keyword: string,
		score: number
	): StressLevelDTO | undefined {
		return stressLevels.find(
			(sl) => sl.description.toUpperCase().includes(keyword.toUpperCase()) && sl.score === score
		);
	}

	private calculateNiosh(data: ErgonomicToolsDTO, stressLevels: StressLevelDTO[]): StressLevelDTO | undefined {
		const risk = (data as unknown as { risk: string })?.risk;
		if (!risk) {
			return undefined;
		}

		if (risk === 'LOW') {
			return this.findStressLevel(stressLevels, 'LI', 1);
		}

		if (risk === 'MODERATE') {
			return this.findStressLevel(stressLevels, 'LI', 3);
		}

		if (risk === 'HIGH') {
			return this.findStressLevel(stressLevels, 'LI', 4);
		}

		return this.findStressLevel(stressLevels, 'LI', 5);
	}

	private calculateKimMho(data: ErgonomicToolsDTO, stressLevels: StressLevelDTO[]): StressLevelDTO | undefined {
		const riskScore = Number((data as any)?.risk_score);
		if (isNaN(riskScore)) {
			return undefined;
		}

		if (riskScore >= 60) {
			return this.findStressLevel(stressLevels, 'LI', 5);
		}

		if (riskScore >= 50) {
			return this.findStressLevel(stressLevels, 'LI', 4);
		}

		if (riskScore >= 25) {
			return this.findStressLevel(stressLevels, 'LI', 3);
		}

		if (riskScore >= 20) {
			return this.findStressLevel(stressLevels, 'LI', 2);
		}

		return this.findStressLevel(stressLevels, 'LI', 1);
	}

	private calculateStrainIndex(data: ErgonomicToolsDTO, stressLevels: StressLevelDTO[]): StressLevelDTO | undefined {
		const leftRsi = Number((data as any)?.score_left_rsi);
		const rightRsi = Number((data as any)?.score_right_rsi);
		if (isNaN(leftRsi) && isNaN(rightRsi)) {
			return undefined;
		}

		const worst = Math.max(leftRsi || 0, rightRsi || 0);

		if (worst >= 12) {
			return this.findStressLevel(stressLevels, 'Strain', 5);
		}

		if (worst >= 7) {
			return this.findStressLevel(stressLevels, 'Strain', 4);
		}

		if (worst >= 5) {
			return this.findStressLevel(stressLevels, 'Strain', 3);
		}

		if (worst >= 3) {
			return this.findStressLevel(stressLevels, 'Strain', 2);
		}

		return this.findStressLevel(stressLevels, 'Strain', 1);
	}

	private calculateKimPp(data: ErgonomicToolsDTO, stressLevels: StressLevelDTO[]): StressLevelDTO | undefined {
		const score = Number((data as any)?.score);
		if (isNaN(score)) {
			return undefined;
		}

		if (score >= 60) {
			return this.findStressLevel(stressLevels, 'LI', 5);
		}

		if (score >= 50) {
			return this.findStressLevel(stressLevels, 'LI', 4);
		}

		if (score >= 25) {
			return this.findStressLevel(stressLevels, 'LI', 3);
		}

		if (score >= 20) {
			return this.findStressLevel(stressLevels, 'LI', 2);
		}

		return this.findStressLevel(stressLevels, 'LI', 1);
	}
}
